import React, { useState, useEffect } from 'react';
import useSessionStorage from '../hooks/useSessionStorage';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  LineChart, Line, Legend
} from 'recharts';
import { Users, DollarSign, Activity, TrendingUp, PiggyBank, Calendar, Shield, Wrench } from 'lucide-react';
import { tenantsService, expensesService, invoicesService } from '../services/supabaseService';
import { isPersonalCategory } from '../utils/expenseCategories';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parseDateSafe = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  const datePart = typeof dateStr === 'string' ? dateStr.split('T')[0].split(' ')[0] : '';
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    tenants: 0,
    expenses: 0,
    personalExpenses: 0,
    dailyExpenses: 0,
    revenue: 0,
    rent: 0,
    securityDeposit: 0,
    vacatedSecurityDeposit: 0,
    maintenanceFee: 0,
    vacatedMaintenanceFee: 0,
    profit: 0,
    occupancy: 0,
    monthlyExpenses: MONTHS.map(m => ({ name: m, currentYear: 0, lastYear: 0 })),
    monthlyRevenue: MONTHS.map(m => ({ name: m, currentYear: 0, lastYear: 0 }))
  });
  
  const [selectedYear, setSelectedYear] = useSessionStorage('dashboard_selectedYear', new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useSessionStorage('dashboard_selectedMonth', '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [tenants, expenses, invoices] = await Promise.all([
          tenantsService.getAll(),
          expensesService.getAll(),
          invoicesService.getAll()
        ]);

        const yearNum = parseInt(selectedYear, 10);
        const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        // Active tenants
        const activeTenants = (tenants || []).filter(t => !t.vacated);
        const totalTenantsCount = activeTenants.length;

        // Daily PG business expenses (excluding personal categories)
        const todayExpensesSum = (expenses || [])
          .filter(e => !isPersonalCategory(e.category) && e.expense_date && e.expense_date.startsWith(todayStr))
          .reduce((sum, e) => sum + Number(e.amount || 0), 0);

        // Filtered year expenses
        const monthlyExp = new Array(12).fill(0);
        let totalExpYear = 0;
        let totalPersonalExpYear = 0;

        (expenses || []).forEach(e => {
          if (!e.expense_date) return;
          const d = parseDateSafe(e.expense_date);
          if (d && d.getFullYear() === yearNum) {
            const amt = Number(e.amount || 0);
            const isPersonal = isPersonalCategory(e.category);
            const matchesMonth = !selectedMonth || (d.getMonth() + 1) === parseInt(selectedMonth, 10);

            if (isPersonal) {
              if (matchesMonth) {
                totalPersonalExpYear += amt;
              }
            } else {
              if (matchesMonth) {
                totalExpYear += amt;
              }
              monthlyExp[d.getMonth()] += amt;
            }
          }
        });

        // Invoices / Revenue
        const monthlyRev = new Array(12).fill(0);
        let rentCollected = 0;

        (invoices || []).forEach(inv => {
          if (!inv.payment_date || inv.status === 'Unpaid') return;
          const d = parseDateSafe(inv.payment_date);
          if (d && d.getFullYear() === yearNum) {
            const rent = Number(inv.monthly_rent || 0);
            const matchesMonth = !selectedMonth || (d.getMonth() + 1) === parseInt(selectedMonth, 10);

            if (matchesMonth) {
              rentCollected += rent;
            }

            // Monthly Revenue trend: Rent only (Maint & Sec Deposit excluded from Net Profit)
            monthlyRev[d.getMonth()] += rent;
          }
        });

        // Total Security Deposits & Maintenance Fees from Present Tenants at the Moment (Excluding Vacated)
        const presentSecDeposit = activeTenants.reduce((sum, t) => sum + Number(t.security_deposit || 0), 0);
        const presentMaintenanceFee = activeTenants.reduce((sum, t) => sum + Number(t.maintenance_fees || 0), 0);

        const totalRev = rentCollected;
        const netProfit = totalRev - totalExpYear;
        const totalCapacity = 40; // PG assumed capacity
        const occupancyRate = Math.min(100, Math.round((totalTenantsCount / totalCapacity) * 100));

        setStats({
          tenants: totalTenantsCount,
          expenses: totalExpYear,
          personalExpenses: totalPersonalExpYear,
          dailyExpenses: todayExpensesSum,
          revenue: totalRev,
          rent: rentCollected,
          securityDeposit: presentSecDeposit,
          maintenanceFee: presentMaintenanceFee,
          profit: netProfit,
          occupancy: occupancyRate,
          monthlyExpenses: MONTHS.map((m, i) => ({ name: m, currentYear: monthlyExp[i], lastYear: 0 })),
          monthlyRevenue: MONTHS.map((m, i) => ({ name: m, currentYear: monthlyRev[i], lastYear: 0 }))
        });
      } catch (err) {
        console.error('Error fetching dashboard stats from Supabase:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, [selectedYear, selectedMonth]);

  if (loading) {
    return <div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>Loading dashboard...</div>;
  }

  const revenueTrend = stats.monthlyRevenue && stats.monthlyRevenue.length > 0
    ? stats.monthlyRevenue
    : stats.monthlyExpenses.map(item => ({
        name: item.name,
        currentYear: 0,
        lastYear: 0
      }));

  const handleGenerateReport = () => {
    const rows = [
      ['PG Management Financial Report'],
      ['Generated On', new Date().toLocaleDateString()],
      [],
      ['Key Performance Indicators', 'Value'],
      ['Net Profit (Rent - Expenses)', `Rs ${stats.profit}`],
      ['Total Revenue (Rent)', `Rs ${stats.revenue}`],
      ['Rent Collected', `Rs ${stats.rent}`],
      ['Present Maintenance Fees (Separate)', `Rs ${stats.maintenanceFee}`],
      ['Security Deposits (Reserved Fund)', `Rs ${stats.securityDeposit}`],
      ['Total PG Business Expenses', `Rs ${stats.expenses}`],
      ['Personal Expenses (Deducted)', `Rs ${stats.personalExpenses || 0}`],
      ['Daily Expenses (Today)', `Rs ${stats.dailyExpenses}`],
      ['Active Tenants', stats.tenants],
      ['Occupancy Rate (%)', `${stats.occupancy}%`],
      [],
      ['Monthly PG Expense Breakdown', 'Amount']
    ];

    stats.monthlyExpenses.forEach(month => {
      rows.push([month.name, `Rs ${month.currentYear || 0}`]);
    });

    const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `PG_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5 flex-wrap gap-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Dashboard Overview</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', color: 'var(--primary-color)', padding: '0.4rem 0.85rem', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '600' }}>
              <Users size={16} /> {stats.tenants} Active Tenants
            </div>
          </div>
          <p style={{ fontSize: '0.85rem', margin: '0.75rem 0', color: 'var(--text-secondary)' }}>Welcome back, Admin. Real-time financial and occupancy status.</p>
        </div>
        <div className="flex items-center flex-wrap" style={{ gap: '1rem' }}>
          <select
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            style={{ padding: '0.4rem 0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)', fontSize: '0.875rem' }}
          >
            <option value="" style={{ color: '#000' }}>All Months</option>
            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
              <option key={m} value={i + 1} style={{ color: '#000' }}>{m}</option>
            ))}
          </select>
          <select 
            value={selectedYear} 
            onChange={e => setSelectedYear(parseInt(e.target.value))}
            style={{ padding: '0.4rem 0.75rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)', fontSize: '0.875rem' }}
          >
            {[...Array(6)].map((_, i) => {
              const year = new Date().getFullYear() - i;
              return <option key={year} value={year} style={{ color: '#000' }}>{year}</option>;
            })}
          </select>
          <button className="btn-primary flex items-center gap-2" onClick={handleGenerateReport} style={{ padding: '0.4rem 0.75rem', fontSize: '0.875rem' }}>
            <Activity size={14} /> Generate CSV
          </button>
        </div>
      </div>

      {/* KPI Cards: 8 Cards perfectly aligned in 2 rows of 4 columns */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        
        {/* ROW 1: High Level Financials */}
        <div className="card flex items-center gap-6" style={{ padding: '1.25rem', borderTop: '3px solid #8b5cf6' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PiggyBank size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Net Profit</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: stats.profit >= 0 ? 'var(--accent-color)' : 'var(--danger-color)' }}>
              ₹{stats.profit.toLocaleString()}
            </div>
            <div style={{ fontSize: '0.7rem', color: '#10b981', marginTop: '0.2rem' }}>
              Rent - PG Expenses
            </div>
          </div>
        </div>

        <div className="card flex items-center gap-6" style={{ padding: '1.25rem' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: 'var(--accent-color)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TrendingUp size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Total Revenue</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>₹{stats.revenue.toLocaleString()}</div>
          </div>
        </div>

        <div className="card flex items-center gap-6" style={{ padding: '1.25rem' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger-color)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DollarSign size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Total PG Expenses</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>₹{stats.expenses.toLocaleString()}</div>
            {stats.personalExpenses > 0 && (
              <div style={{ fontSize: '0.7rem', color: '#ec4899', marginTop: '0.2rem' }}>
                -₹{stats.personalExpenses.toLocaleString()} Personal Deducted
              </div>
            )}
          </div>
        </div>

        {/* Daily Expenses */}
        <div className="card flex items-center gap-6" style={{ padding: '1.25rem' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger-color)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Calendar size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Daily Exps (Today)</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>₹{stats.dailyExpenses.toLocaleString()}</div>
          </div>
        </div>

        {/* Revenue Breakdown */}
        <div className="card flex items-center gap-6" style={{ padding: '1.25rem' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', color: 'var(--primary-color)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Activity size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Rent Collected</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>₹{stats.rent.toLocaleString()}</div>
          </div>
        </div>

        <div className="card flex items-center gap-6" style={{ padding: '1.25rem' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Wrench size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Present Maint. Fees</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>₹{stats.maintenanceFee.toLocaleString()}</div>
            <div style={{ fontSize: '0.7rem', color: '#f59e0b', marginTop: '0.2rem' }}>
              {stats.tenants} Present Tenants • Excl. from Profit
            </div>
          </div>
        </div>

        <div className="card flex items-center gap-6" style={{ padding: '1.25rem' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(236, 72, 153, 0.1)', color: '#ec4899', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Sec. Deposits (Reserved Fund)</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>₹{stats.securityDeposit.toLocaleString()}</div>
            <div style={{ fontSize: '0.7rem', color: '#ec4899', marginTop: '0.2rem' }}>
              Reserved Fund • {stats.tenants} Present Tenants
            </div>
          </div>
        </div>

        {/* Property Stats */}
        <div className="card flex items-center gap-6" style={{ padding: '1.25rem' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: 'var(--accent-color)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Activity size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>Occupancy Rate</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>{stats.occupancy}%</div>
          </div>
        </div>

      </div>

      {/* Charts */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        <div className="card" style={{ padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '1.25rem', marginTop: 0, color: 'var(--text-primary)', fontWeight: '600' }}>Expense Analysis (Monthly)</h3>
          <div style={{ height: '280px', width: '100%', minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <BarChart data={stats.monthlyExpenses} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                <XAxis dataKey="name" stroke="var(--text-secondary)" tick={{ fontSize: 12 }} />
                <YAxis stroke="var(--text-secondary)" tick={{ fontSize: 12 }} />
                <RechartsTooltip 
                  contentStyle={{ backgroundColor: 'var(--surface-color)', borderColor: 'var(--border-color)', borderRadius: '0.5rem', color: '#fff', fontSize: '0.85rem' }}
                  itemStyle={{ color: '#fff' }}
                  formatter={(value, name) => [`₹${value}`, name]}
                />
                <Legend wrapperStyle={{ fontSize: '0.85rem', paddingTop: '10px' }} />
                <Bar dataKey="currentYear" name={selectedYear.toString()} fill="var(--danger-color)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                <Bar dataKey="lastYear" name={(selectedYear - 1).toString()} fill="var(--text-secondary)" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card" style={{ padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '1.25rem', marginTop: 0, color: 'var(--text-primary)', fontWeight: '600' }}>Revenue Trend (Monthly)</h3>
          <div style={{ height: '280px', width: '100%', minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <LineChart data={revenueTrend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                <XAxis dataKey="name" stroke="var(--text-secondary)" tick={{ fontSize: 12 }} />
                <YAxis stroke="var(--text-secondary)" tick={{ fontSize: 12 }} />
                <RechartsTooltip 
                  contentStyle={{ backgroundColor: 'var(--surface-color)', borderColor: 'var(--border-color)', borderRadius: '0.5rem', color: '#fff', fontSize: '0.85rem' }}
                  itemStyle={{ color: '#fff' }}
                  formatter={(value, name) => [`₹${value.toLocaleString()}`, name]}
                />
                <Legend wrapperStyle={{ fontSize: '0.85rem', paddingTop: '10px' }} />
                <Line type="monotone" dataKey="currentYear" name={selectedYear.toString()} stroke="var(--accent-color)" strokeWidth={2} dot={{ fill: 'var(--accent-color)', r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="lastYear" name={(selectedYear - 1).toString()} stroke="var(--text-secondary)" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: 'var(--text-secondary)', r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1rem', marginBottom: '1.25rem', marginTop: 0, color: 'var(--text-primary)', fontWeight: '600' }}>Monthly Expenses Trend (Line Chart)</h3>
        <div style={{ height: '280px', width: '100%', minWidth: 0 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <LineChart data={stats.monthlyExpenses} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
              <XAxis dataKey="name" stroke="var(--text-secondary)" tick={{ fontSize: 12 }} />
              <YAxis stroke="var(--text-secondary)" tick={{ fontSize: 12 }} />
              <RechartsTooltip 
                contentStyle={{ backgroundColor: 'var(--surface-color)', borderColor: 'var(--border-color)', borderRadius: '0.5rem', color: '#fff', fontSize: '0.85rem' }}
                itemStyle={{ color: '#fff' }}
                formatter={(value, name) => [`₹${value.toLocaleString()}`, name]}
              />
              <Legend wrapperStyle={{ fontSize: '0.85rem', paddingTop: '10px' }} />
              <Line type="monotone" dataKey="currentYear" name={selectedYear.toString()} stroke="var(--danger-color)" strokeWidth={2} dot={{ fill: 'var(--danger-color)', r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="lastYear" name={(selectedYear - 1).toString()} stroke="var(--text-secondary)" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: 'var(--text-secondary)', r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
